// src/pages/resume/education.js
import React, { useCallback, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import AddItemButton from '../../components/common/AddItemButton';
import FormInput from '../../components/resume/FormInput';
import DatePicker from '../../components/resume/DatePicker';
import { useResume } from '../../context/ResumeContext';
import useAutosave from '../../hooks/useAutosave';
import SaveStatusChip from '../../components/resume/editor/SaveStatusChip';
import FeedbackModal from '../../components/common/FeedbackModal';

const EducationItem = ({ education, index, onUpdate, onDelete }) => {
    const schoolName = education.school || 'YOUR SCHOOL';

    return (
        <div className="bg-white/[0.04] border border-white/10 rounded-xl p-6 mb-6">
            <div className="flex justify-between items-start mb-6">
                <h2 className="text-xl font-bold">
                    {education.degree || `Education ${index + 1}`}
                </h2>
                <button onClick={() => onDelete(education.id)} className="bg-white/10 hover:bg-red-500/20 hover:text-red-300 text-gray-400 flex-shrink-0 ml-4 rounded p-1">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                </button>
            </div>
            <form className="space-y-6">
                <FormInput label="WHAT WAS YOUR DEGREE OR FIELD OF STUDY? *" name="degree" value={education.degree} onChange={(e) => onUpdate(education.id, { ...education, degree: e.target.value })} placeholder="B.S. in Computer Science" />
                <FormInput label="WHERE DID YOU GO TO SCHOOL? *" name="school" value={education.school} onChange={(e) => onUpdate(education.id, { ...education, school: e.target.value })} placeholder="University of Washington" />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label className="block text-xs font-bold text-gray-400 uppercase mb-2">{`WHEN DID YOU ATTEND ${schoolName}?`}</label>
                        <div className="flex items-center gap-4">
                            <DatePicker
                                value={education.startDate}
                                onSelect={(date) => onUpdate(education.id, { ...education, startDate: date })}
                            />
                            <DatePicker
                                value={education.endDate}
                                onSelect={(date) => onUpdate(education.id, { ...education, endDate: date, isCurrent: false })}
                                startDate={education.startDate}
                                showToggle={true}
                                isCurrent={education.isCurrent}
                                onToggleCurrent={() => onUpdate(education.id, { ...education, isCurrent: !education.isCurrent, endDate: !education.isCurrent ? 'Present' : '' })}
                                toggleLabel="Currently studying here"
                            />
                        </div>
                    </div>
                    <FormInput label="WHERE WAS THE SCHOOL LOCATED?" name="location" value={education.location} onChange={(e) => onUpdate(education.id, { ...education, location: e.target.value })} placeholder="Seattle, WA" />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormInput label="Minor" name="minor" value={education.minor} onChange={(e) => onUpdate(education.id, { ...education, minor: e.target.value })} placeholder="e.g., Mathematics" />
                    <FormInput label="GPA" name="gpa" value={education.gpa} onChange={(e) => onUpdate(education.id, { ...education, gpa: e.target.value })} placeholder="e.g., 3.8/4.0" />
                </div>
            </form>
        </div>
    );
};

const Education = () => {
    const { educations, setEducations, addEducation } = useResume();
    const { resumeId } = useParams();
    const [modalInfo, setModalInfo] = useState({ isOpen: false, title: '', message: '', isError: false });
    const idMapRef = useRef(new Map());

    const updateEducation = useCallback((id, updatedData) => {
        setEducations(currentEducations =>
            currentEducations.map(edu => (edu.id === id ? updatedData : edu))
        );
    }, [setEducations]);

    const deleteEducation = (id) => {
        if (educations.length > 1) {
            setEducations(educations.filter(edu => edu.id !== id));
        } else {
            setModalInfo({ isOpen: true, title: 'Notice', message: 'You must have at least one education entry.', isError: false });
        }
    };

    const saveAllEducations = async () => {
        if (!resumeId) throw new Error('no resume ID');

        let saveErrors = 0;
        const savePromises = educations.map(edu => {
            if (edu.degree || edu.school) {
                const effectiveId = idMapRef.current.get(edu.id) ?? edu.id;
                return fetch('https://renaisons.com/api/save_education.php', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ ...edu, id: effectiveId, resume_id: resumeId }),
                })
                    .then(response => response.json())
                    .then(result => {
                        if (result.status === 'success' && result.education_id) {
                            idMapRef.current.set(edu.id, result.education_id);
                            return { oldId: edu.id, newId: result.education_id };
                        } else if (result.status !== 'success') {
                            saveErrors++;
                            console.error(`Error saving education "${edu.school || 'New'}": ${result.message}`);
                        }
                        return null;
                    })
                    .catch(error => {
                        saveErrors++;
                        console.error(`Network error saving education "${edu.school || 'New'}":`, error);
                        return null;
                    });
            }
            return Promise.resolve(null);
        });

        try {
            const results = await Promise.all(savePromises);

            const updates = results.filter(Boolean);
            if (updates.length > 0) {
                setEducations(currentEducations =>
                    currentEducations.map(edu => {
                        const update = updates.find(u => u.oldId === edu.id);
                        return update ? { ...edu, id: update.newId } : edu;
                    })
                );
            }

            if (saveErrors > 0) {
                throw new Error('some entries failed');
            }

        } catch (error) {
            console.error('Error during batch save:', error);
            throw error;
        }
    };

    const validateEducations = () => {
        const hasAnyData = educations.some(edu =>
            edu.degree || edu.school || edu.startDate || edu.endDate || edu.location || edu.minor || edu.gpa
        );
        if (!hasAnyData) return null;
        const hasInvalid = educations.some(edu =>
            (edu.startDate || edu.endDate || edu.location || edu.minor || edu.gpa) && !edu.degree && !edu.school
        );
        return hasInvalid ? false : true;
    };

    const { status, retry } = useAutosave({ sectionKey: 'education', data: educations, save: saveAllEducations, validate: validateEducations });

    return (
        <>
            {modalInfo.isOpen && (
                <FeedbackModal
                    title={modalInfo.title}
                    message={modalInfo.message}
                    isError={modalInfo.isError}
                    onClose={() => setModalInfo({ ...modalInfo, isOpen: false })}
                />
            )}
            {educations.map((edu, index) => (
                <EducationItem
                    key={edu.id}
                    education={edu}
                    index={index}
                    onUpdate={updateEducation}
                    onDelete={deleteEducation}
                />
            ))}
            <div className="flex items-center justify-between mt-4">
                <AddItemButton onClick={addEducation}>ADD ANOTHER EDUCATION</AddItemButton>
                <SaveStatusChip status={status} onRetry={retry} />
            </div>
        </>
    );
};

export default Education;
